import { metricsConfig } from "../../config/widget-system.config";
import {
  WidgetSystemMetrics,
  WidgetSystemStats,
} from "../../types/widget-systems";
import { widgetLogger } from "./WidgetLogger";

interface MetricData {
  timestamp: number;
  value: number;
}

class WidgetMetrics {
  private static instance: WidgetMetrics;
  private metrics: Map<string, MetricData[]> = new Map();
  private stats: WidgetSystemStats = {
    totalWidgets: 0,
    activeWidgets: 0,
    cachedDataItems: 0,
    totalLogs: 0,
    errors: 0,
    warnings: 0,
  };

  private constructor() {
    this.startMetricsCollection();
  }

  static getInstance(): WidgetMetrics {
    if (!WidgetMetrics.instance) {
      WidgetMetrics.instance = new WidgetMetrics();
    }
    return WidgetMetrics.instance;
  }

  private startMetricsCollection(): void {
    setInterval(() => {
      this.collectMetrics();
    }, metricsConfig.collectionInterval);
  }

  private collectMetrics(): void {
    const now = Date.now();
    this.cleanupOldMetrics(now);

    // Собираем метрики
    const metrics: WidgetSystemMetrics = {
      averageLoadTime: this.calculateAverageLoadTime(),
      cacheHitRate: this.calculateCacheHitRate(),
      errorRate: this.calculateErrorRate(),
      refreshRate: this.calculateRefreshRate(),
    };

    // Проверяем пороговые значения
    this.checkAlertThresholds(metrics);

    // Логируем метрики
    widgetLogger.info("system", "Метрики системы виджетов", {
      metrics,
      stats: this.stats,
    });
  }

  private cleanupOldMetrics(now: number): void {
    const cutoff = now - metricsConfig.retentionPeriod;
    Array.from(this.metrics.entries()).forEach(([key, data]) => {
      this.metrics.set(
        key,
        data.filter((item: MetricData) => item.timestamp > cutoff)
      );
    });
  }

  private calculateAverageLoadTime(): number {
    const loadTimes = this.metrics.get("loadTime") || [];
    if (loadTimes.length === 0) return 0;
    return (
      loadTimes.reduce((sum, item) => sum + item.value, 0) / loadTimes.length
    );
  }

  private calculateCacheHitRate(): number {
    const hits = this.metrics.get("cacheHits") || [];
    const misses = this.metrics.get("cacheMisses") || [];
    const total = hits.length + misses.length;
    return total === 0 ? 0 : hits.length / total;
  }

  private calculateErrorRate(): number {
    const errors = this.metrics.get("errors") || [];
    const total = this.metrics.get("total") || [];
    return total.length === 0 ? 0 : errors.length / total.length;
  }

  private calculateRefreshRate(): number {
    const refreshes = this.metrics.get("refreshes") || [];
    const timeSpan = metricsConfig.collectionInterval;
    return refreshes.length / (timeSpan / 1000);
  }

  private checkAlertThresholds(metrics: WidgetSystemMetrics): void {
    const { alertThresholds } = metricsConfig;

    if (metrics.errorRate > alertThresholds.errorRate) {
      widgetLogger.warn("system", "Высокий уровень ошибок", {
        errorRate: metrics.errorRate,
      });
    }

    if (metrics.averageLoadTime > alertThresholds.loadTime) {
      widgetLogger.warn("system", "Высокое время загрузки", {
        loadTime: metrics.averageLoadTime,
      });
    }

    if (metrics.cacheHitRate < alertThresholds.cacheHitRate) {
      widgetLogger.warn("system", "Низкий уровень кэширования", {
        cacheHitRate: metrics.cacheHitRate,
      });
    }
  }

  // Публичные методы для сбора метрик

  recordLoadTime(_widgetId: string, loadTime: number): void {
    this.recordMetric("loadTime", loadTime);
  }

  recordCacheHit(_widgetId: string): void {
    this.recordMetric("cacheHits", 1);
  }

  recordCacheMiss(_widgetId: string): void {
    this.recordMetric("cacheMisses", 1);
  }

  recordError(_widgetId: string): void {
    this.recordMetric("errors", 1);
    this.stats.errors++;
  }

  recordRefresh(_widgetId: string): void {
    this.recordMetric("refreshes", 1);
  }

  recordTotal(_widgetId: string): void {
    this.recordMetric("total", 1);
  }

  private recordMetric(name: string, value: number): void {
    const now = Date.now();
    const data = this.metrics.get(name) || [];
    data.push({ timestamp: now, value });
    this.metrics.set(name, data);
  }

  updateStats(stats: Partial<WidgetSystemStats>): void {
    this.stats = { ...this.stats, ...stats };
  }

  getStats(): WidgetSystemStats {
    return { ...this.stats };
  }

  getMetrics(): WidgetSystemMetrics {
    return {
      averageLoadTime: this.calculateAverageLoadTime(),
      cacheHitRate: this.calculateCacheHitRate(),
      errorRate: this.calculateErrorRate(),
      refreshRate: this.calculateRefreshRate(),
    };
  }
}

export const widgetMetrics = WidgetMetrics.getInstance();
